import { createRoute, Outlet } from "@tanstack/react-router";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import { useBasePath } from "../../hooks/router";
import { memo } from "react";
import { adminRoutes, userRoutes } from "../../App";
import { Preferences } from "./Preferences";
import { Authentication } from "./Authentication";
import type { IntlShape } from "react-intl";
import { useIntl } from "react-intl";
import { memoize } from "es-toolkit";
import { MenuType, type NavMenuOptions } from "../../types/menu";
import { IconArrowLeft, IconLock, IconPencilStar } from "@tabler/icons-react";
import { useSessionAccountName } from "../../hooks/user";

const profileMenu = memoize(({ basePath, accountName, intl }: { basePath: string; accountName?: string; intl: IntlShape }): NavMenuOptions => ({
  items: [
    {
      title: intl.formatMessage({
        defaultMessage: "Back",
        id: "navigation.back",
      }),
      type: MenuType.Single,
      Icon: IconArrowLeft,
      to: `${basePath}/${accountName}`,
    },
    {
      type: MenuType.Divider,
    },
    {
      title: intl.formatMessage({
        defaultMessage: "Preferences",
        id: "navigation.preferences",
      }),
      type: MenuType.Single,
      Icon: IconPencilStar,
      to: "/profile",
    },
    {
      title: intl.formatMessage({
        defaultMessage: "Security",
        id: "navigation.security",
      }),
      type: MenuType.Single,
      Icon: IconLock,
      to: "/profile/security",
    },
  ],
}), { getCacheKey: ({ basePath, accountName, intl }) => `${basePath}-${accountName}-${intl.locale}` });

const userProfileLayoutRoute = createRoute({
  getParentRoute: () => userRoutes,
  id: "profile-dashboard",
  component: memo(() => {
    const basePath = useBasePath();
    const intl = useIntl();
    const accountName = useSessionAccountName();

    return (
      <DashboardLayout menu={profileMenu({ basePath, accountName, intl })}>
        <Outlet />
      </DashboardLayout>
    );
  }),
});

const userProfileIndexRoute = createRoute({
  getParentRoute: () => userProfileLayoutRoute,
  path: "/profile",
  component: Preferences,
});

const userProfileSecurityRoute = createRoute({
  getParentRoute: () => userProfileLayoutRoute,
  path: "/profile/security",
  component: Authentication,
});

export const userProfileRoutes = userProfileLayoutRoute.addChildren([
  userProfileIndexRoute,
  userProfileSecurityRoute,
]);

const adminProfileLayoutRoute = createRoute({
  getParentRoute: () => adminRoutes,
  id: "profile-dashboard",
  component: memo(() => {
    const basePath = useBasePath();
    const intl = useIntl();
    const accountName = useSessionAccountName();
    return (
      <DashboardLayout menu={profileMenu({ basePath, accountName, intl })}>
        <Outlet />
      </DashboardLayout>
    );
  }),
});

const adminProfileIndexRoute = createRoute({
  getParentRoute: () => adminProfileLayoutRoute,
  path: "/profile",
  component: Preferences,
});

const adminProfileAuthenticationRoute = createRoute({
  getParentRoute: () => adminProfileLayoutRoute,
  path: "/profile/authentication",
  component: Authentication,
});

export const adminProfileRoutes = adminProfileLayoutRoute.addChildren([
  adminProfileIndexRoute,
  adminProfileAuthenticationRoute,
]);
